import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  saveRoomToStorage,
  setIsProjectSwitchInitiated,
} from '@store/actionSlices/appConfig'
import { selectIsShowcaseMultiScreen } from '@store/actionSlices/multiScreen'
import { saveProjectIdentityToStorage } from '@store/actionSlices/projectIdentity'
import { removeShortlist } from '@store/actionSlices/shortlist'
import { RootStateTypeExtra, SessionListContent } from '@store/types'

import {
  Project,
  selectFromResult as selectFromProjectConfigResult,
  useLazyGetConfigQuery,
} from '@api/config'

import {
  removeCacheVersion,
  setCacheVersion,
} from '@utilities/cache-version-utils'
import useDefaultRoom from '@utilities/use-default-room'

import { fetchCacheVersionFromFirebase } from '@firebaseUtil/firebase-cache-version-service'
import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { updateMasterKeyActiveProject } from '@firebaseUtil/firebase-master-key-service'

import { ProjectType } from '@adUtilities/constants'
import {
  ApartmentRouteContext,
  HouseAndLandRouteContext,
} from '@adUtilities/types'

interface ValidGetProjectDetailsReturn {
  success: true
  message?: unknown
  project: Project
  sessionList: SessionListContent[]
  sessionProject: SessionListContent
}

interface InvalidGetProjectDetailsReturn {
  success: false
  message: string
  project?: unknown
  sessionList?: unknown
  sessionProject?: unknown
}

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()

  const appConfig = useSelector((state: RootStateTypeExtra) => state.appConfig)
  const isShowcaseMultiScreen = useSelector(selectIsShowcaseMultiScreen)

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom } = useDefaultRoom()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const getSessionProject = useCallback(
    (project: Project) => {
      const sessionList = projectIdentity.sessionList.map((session) => {
        if (session.projectId === project.canonicalName) {
          return {
            ...session,
            projectName: session.projectName || project.canonicalName,
            projectUUID: session.projectUUID || project.uuid,
          }
        }
        return session
      })
      const sessionProject = sessionList.find(
        (session) => session.projectId === project.canonicalName
      )
      return { sessionProject, sessionList }
    },
    [projectIdentity.sessionList]
  )

  const getProjectDetails = useCallback<
    (args: {
      projectCanonicalName: string
    }) => Promise<ValidGetProjectDetailsReturn | InvalidGetProjectDetailsReturn>
  >(
    async ({ projectCanonicalName }) => {
      const projectConfigResponse = await getProjectConfig({
        projectName: projectCanonicalName,
      })
      const { project } = selectFromProjectConfigResult(projectConfigResponse)

      const { sessionList, sessionProject } = getSessionProject(project)

      if (!project.uuid) {
        return {
          success: false,
          message: 'Project not found',
        }
      }

      if (!sessionProject) {
        return {
          success: false,
          message: 'There are no sessions for the selected project.',
        }
      }

      return {
        success: true,
        project,
        sessionList,
        sessionProject,
      }
    },
    [getProjectConfig, getSessionProject]
  )

  const updateRoom = useCallback<
    (args: { projectName: string }) => Promise<void>
  >(
    async ({ projectName }) => {
      const defaultRoom = await getDefaultRoom({
        projectName,
        prioritizeCurrentRoom: true,
      })
      if (defaultRoom) {
        saveRoomToStorage(appConfig, defaultRoom)
      }
    },
    [getDefaultRoom, appConfig]
  )

  const triggerProjectSwitchInSession = useCallback(
    async ({
      projectId,
      projectLabel,
      projectName,
    }: {
      projectId: string
      projectLabel: string
      projectName: string
    }) => {
      if (projectIdentity.masterKey && !isShowcaseMultiScreen) {
        await updateMasterKeyActiveProject({
          masterKey: projectIdentity.masterKey,
          activeProject: projectName,
        })
      }
      await firebaseControlQuery
        .update({
          [`projectSwitch`]: {
            projectId,
            projectLabel,
            projectName,
          },
        })
        .then(async () => {
          await firebaseControlQuery.update({
            [`projectSwitch`]: {
              projectId: '',
              projectLabel: '',
              projectName: '',
            },
          })
        })
    },
    [firebaseControlQuery]
  )

  const updateApartmentSession = useCallback<
    (args: {
      redirectTo: string
      redirectContext?: ApartmentRouteContext
      projectId: string
      sessionId: string
    }) => Promise<void>
  >(
    async ({ redirectTo, redirectContext, projectId, sessionId }) => {
      const data: Record<string, string> = {
        activeRoute: redirectTo,
      }

      data['building.activeBlock'] = redirectContext?.block || ''
      data['building.activeLevel'] = redirectContext?.level || ''
      data['building.activeUnit'] = redirectContext?.unit || ''

      return firebaseControlQuery.update(data, sessionId, projectId)
    },
    [firebaseControlQuery]
  )

  const updateHouseAndLandSession = useCallback<
    (args: {
      redirectTo: string
      redirectContext?: HouseAndLandRouteContext
      projectId: string
      sessionId: string
    }) => Promise<void>
  >(
    async ({ redirectTo, redirectContext, projectId, sessionId }) => {
      const data: Record<string, string> = {
        activeRoute: redirectTo,
      }

      data['houseAndLand.activePrecinctId'] = redirectContext?.precinct || ''
      data['houseAndLand.activeStageId'] = redirectContext?.stage || ''
      data['houseAndLand.activeLotId'] = redirectContext?.lot || ''

      return firebaseControlQuery.update(data, sessionId, projectId)
    },
    [firebaseControlQuery]
  )

  const redirectToRoute = useCallback<
    (args: {
      projectId: string
      sessionId: string
      projectType: ProjectType
      redirectTo: string
      redirectContext?: ApartmentRouteContext | HouseAndLandRouteContext
    }) => Promise<void>
  >(
    async ({
      projectId,
      sessionId,
      projectType,
      redirectContext,
      redirectTo,
    }) => {
      const updateSession =
        projectType === ProjectType.Apartment
          ? updateApartmentSession
          : updateHouseAndLandSession

      await updateSession({
        redirectContext,
        redirectTo,
        projectId,
        sessionId,
      })
      window.location.href = `/${redirectTo}`
    },
    [updateApartmentSession, updateHouseAndLandSession]
  )

  const refreshPage = useCallback(() => {
    window.location.reload()
  }, [])

  const processProjectSwitch = useCallback<
    (args: {
      projectCanonicalName: string
      redirectTo?: string
      redirectContext?: ApartmentRouteContext | HouseAndLandRouteContext
    }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ projectCanonicalName, redirectTo, redirectContext }) => {
      removeCacheVersion()
      const version = await fetchCacheVersionFromFirebase(projectCanonicalName)
      setCacheVersion(version)

      const { success, message, project, sessionList, sessionProject } =
        await getProjectDetails({
          projectCanonicalName,
        })

      if (!success) {
        return {
          status: false,
          message,
        }
      }

      const projectName = sessionProject.projectId || project.canonicalName

      await updateRoom({
        projectName,
      })

      await triggerProjectSwitchInSession({
        projectId: sessionProject.projectUUID || project.uuid,
        projectName,
        projectLabel: sessionProject.projectName || project.canonicalName,
      })

      saveProjectIdentityToStorage({
        ...projectIdentity,
        sessionList,
        projectId: project.canonicalName,
        projectName: project.canonicalName,
        sessionId: sessionProject.sessionId,
        projectUUID: project.uuid,
        projectLabel: project.title,
      })

      dispatch(removeShortlist())

      if (redirectTo) {
        redirectToRoute({
          projectId: sessionProject.projectId,
          sessionId: sessionProject.sessionId,
          projectType: project.type,
          redirectContext,
          redirectTo,
        })

        return {
          status: true,
        }
      }
      refreshPage()

      return {
        status: true,
      }
    },
    [
      appConfig,
      projectIdentity,
      getProjectDetails,
      updateRoom,
      triggerProjectSwitchInSession,
      redirectToRoute,
      refreshPage,
    ]
  )

  const startProjectSwitch = useCallback<typeof processProjectSwitch>(
    async (...args) => {
      dispatch(setIsProjectSwitchInitiated(true))
      try {
        const result = await processProjectSwitch(...args)
        if (!result.status) {
          dispatch(setIsProjectSwitchInitiated(false))
        }
        return result
      } catch (error) {
        dispatch(setIsProjectSwitchInitiated(false))
        return {
          status: false,
          message: 'Failed to switch project',
        }
      }
    },
    [processProjectSwitch]
  )

  return {
    startProjectSwitch,
  }
}

export default useProjectSwitchUtils
